//*Libraries imports
import React from "react";
import { UploadCloudIcon, EyeIcon, Trash2Icon } from "lucide-react";

type ReceiptUploadAreaProps = {
  selectedFile: File | undefined;
  previewUrl: string | null;
  isDragOver: boolean;
  uploadInProgress: boolean;
  errors: { receipt?: { message?: string } };
  fileInputRef: React.RefObject<HTMLInputElement>;
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onSelectClick: () => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPreviewOpen: () => void;
  onDeleteOpen: () => void;
};

export function ReceiptUploadArea({
  selectedFile,
  previewUrl,
  isDragOver,
  uploadInProgress,
  errors,
  fileInputRef,
  onDrop,
  onDragOver,
  onDragLeave,
  onSelectClick,
  onFileSelect,
  onPreviewOpen,
  onDeleteOpen,
}: ReceiptUploadAreaProps) {
  return (
    <div className="flex flex-col gap-4 pb-12">
      <h2 className="text-lg font-bold text-clarotexto-201">
        Enviar comprovante
      </h2>

      {!selectedFile ? (
        <div
          className={`border-2 border-dashed rounded-2xl p-8 md:p-12 flex flex-col items-center justify-center gap-4 transition-all cursor-pointer
            ${isDragOver ? "border-claroprimaria-01 bg-blue-50 scale-[1.01]" : "border-[#D8E0F0] bg-[#FCFCFC] hover:bg-gray-50"}
            ${errors.receipt ? "border-red-300 bg-red-50" : ""}`}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={onSelectClick}
        >
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,image/jpeg,image/png,application/pdf"
            onChange={onFileSelect}
          />
          <div className="size-14 bg-white rounded-full flex items-center justify-center shadow-md border border-gray-100">
            <UploadCloudIcon className="size-7 text-claroprimaria-01" />
          </div>
          <div className="text-center">
            <p className="text-base font-semibold text-clarotexto-201">
              Clique para fazer upload
            </p>
            <p className="text-sm text-gray-500 mt-1">
              ou arraste e solte o arquivo aqui
            </p>
          </div>
          {errors.receipt && (
            <span className="text-xs text-red-500 font-medium">
              {errors.receipt.message}
            </span>
          )}
        </div>
      ) : (
        <div className="border border-[#EDF2F6] rounded-2xl p-5 flex flex-row items-center gap-4 bg-white shadow-sm">
          <div className="size-16 bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0 relative border border-gray-100">
            {uploadInProgress ? (
              <div className="size-6 border-2 border-claroprimaria-01 border-t-transparent rounded-full animate-spin" />
            ) : selectedFile?.type.startsWith("image/") && previewUrl ? (
              <img
                src={previewUrl}
                alt="Preview"
                className="size-full object-cover"
              />
            ) : (
              <div className="flex flex-col items-center justify-center">
                <span className="text-[10px] font-bold text-gray-400 uppercase">
                  {selectedFile?.name.split(".").pop()}
                </span>
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-clarotexto-201 truncate">
              {selectedFile?.name}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {(selectedFile.size / 1024).toFixed(1)} KB •{" "}
              {uploadInProgress ? "Enviando..." : "Pronto para enviar"}
            </p>
          </div>

          <div className="flex flex-row items-center gap-1">
            {previewUrl && !uploadInProgress && (
              <button
                type="button"
                className="p-2.5 hover:bg-gray-100 rounded-xl text-gray-500 hover:text-claroprimaria-01 transition-colors"
                onClick={onPreviewOpen}
                title="Visualizar"
              >
                <EyeIcon className="size-5" />
              </button>
            )}
            <button
              className="p-2.5 hover:bg-red-50 rounded-xl text-gray-400 hover:text-red-500 transition-colors"
              onClick={onDeleteOpen}
              title="Remover"
            >
              <Trash2Icon className="size-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
